import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './styles.css';

/**
 * PointerArrow Component
 * 
 * A reusable animated arrow indicator that points at UI elements.
 * The arrow tip touches the target element edge.
 * 
 * @param {boolean} isVisible - Whether the arrow is visible
 * @param {function} onDismiss - Callback when arrow is dismissed (click anywhere)
 * @param {string} label - Text to display (default: "Click here!")
 * @param {string} direction - Arrow direction: 'up', 'down', 'left', 'right' (default: 'up')
 * @param {string} targetSelector - CSS selector for the target element to point at
 * @param {HTMLElement} targetElement - Direct reference to target element (alternative to selector)
 * @param {string} variant - Color variant: 'primary', 'success', 'warning' (default: 'primary')
 */
const PointerArrow = ({ 
  isVisible, 
  onDismiss, 
  label = 'Click here!',
  direction = 'up',
  targetSelector,
  targetElement,
  variant = 'primary',
}) => {
  const [position, setPosition] = useState(null);

  // Calculate position - arrow tip should touch target edge
  const updatePosition = useCallback(() => {
    let target = targetElement;
    
    if (!target && targetSelector) {
      target = document.querySelector(targetSelector);
    }
    
    if (!target) {
      setPosition(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    let pos = { left: 0, top: 0 };

    switch (direction) {
      case 'up':
        // Arrow points up at target, positioned at bottom edge of target
        pos = {
          left: rect.left + rect.width / 2,
          top: rect.bottom,
        };
        break;
      case 'down':
        // Arrow points down at target, positioned at top edge of target
        pos = {
          left: rect.left + rect.width / 2,
          top: rect.top,
        };
        break;
      case 'left':
        // Arrow points left at target, positioned at right edge of target
        pos = {
          left: rect.right,
          top: rect.top + rect.height / 2,
        };
        break;
      case 'right':
        // Arrow points right at target, positioned at left edge of target
        pos = {
          left: rect.left,
          top: rect.top + rect.height / 2,
        };
        break;
      default:
        pos = {
          left: rect.left + rect.width / 2,
          top: rect.bottom,
        };
    }

    setPosition(pos);
  }, [targetSelector, targetElement, direction]);

  // Update position when visible
  useEffect(() => {
    if (!isVisible) {
      setPosition(null);
      return;
    }

    // Initial position
    updatePosition();

    // Update on resize/scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isVisible, updatePosition]);

  // Dismiss on any click (with delay to avoid catching the triggering click)
  useEffect(() => {
    if (!isVisible) return;

    const handleDismiss = () => {
      onDismiss?.();
    };

    // Delay adding the listener to avoid catching the click that triggered visibility
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleDismiss, true);
      document.addEventListener('mousedown', handleDismiss, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleDismiss, true);
      document.removeEventListener('mousedown', handleDismiss, true);
    };
  }, [isVisible, onDismiss]);

  if (!isVisible || !position) return null;

  // Get animation class based on direction
  const getAnimationClass = () => {
    switch (direction) {
      case 'up': return 'pointer-arrow--bob-down';
      case 'down': return 'pointer-arrow--bob-up';
      case 'left': return 'pointer-arrow--bob-right';
      case 'right': return 'pointer-arrow--bob-left';
      default: return 'pointer-arrow--bob-down';
    }
  };

  // Render arrow SVG - simple triangle pointing at target
  const renderArrow = () => {
    // Triangle dimensions
    const width = 16;
    const height = 10;
    
    const triangles = {
      up: (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="currentColor">
          <path d={`M${width/2} 0L${width} ${height}H0L${width/2} 0Z`} />
        </svg>
      ),
      down: (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="currentColor">
          <path d={`M${width/2} ${height}L0 0H${width}L${width/2} ${height}Z`} />
        </svg>
      ),
      left: (
        <svg width={height} height={width} viewBox={`0 0 ${height} ${width}`} fill="currentColor">
          <path d={`M0 ${width/2}L${height} 0V${width}L0 ${width/2}Z`} />
        </svg>
      ),
      right: (
        <svg width={height} height={width} viewBox={`0 0 ${height} ${width}`} fill="currentColor">
          <path d={`M${height} ${width/2}L0 ${width}V0L${height} ${width/2}Z`} />
        </svg>
      ),
    };

    return triangles[direction] || triangles.up;
  };

  // Render via portal to ensure it's above everything
  return ReactDOM.createPortal(
    <div 
      className={`pointer-arrow pointer-arrow--${direction} pointer-arrow--${variant} ${getAnimationClass()}`}
      style={{
        left: position.left,
        top: position.top,
      }}
    >
      <div className="pointer-arrow__container">
        <div className="pointer-arrow__icon">
          {renderArrow()}
        </div>
        {label && (
          <div className="pointer-arrow__label">
            {label}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PointerArrow;
